import React from 'react';
import { Box, useTheme } from '@mui/material';

import type { PageContainerProps } from './PageContainer.types';

/**
 * PageContainer Component
 *
 * A flexible page container wrapper that provides consistent layout,
 * spacing, and centering for page content. Follows the pattern from
 * PatientListPage and other feature pages.
 *
 * @example
 * ```tsx
 * <PageContainer>
 *   <PageHeader title="My Page" />
 *   <Content />
 * </PageContainer>
 * ```
 *
 * @example
 * ```tsx
 * <PageContainer maxWidth={1200} fullHeight>
 *   <CenteredContent />
 * </PageContainer>
 * ```
 */
export const PageContainer: React.FC<PageContainerProps> = ({
  maxWidth = 1000,
  fullHeight = false,
  children,
  sx,
  ...props
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        maxWidth,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        ...(fullHeight && {
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
        }),
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};
